import React, { useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Input,
  Box,
  VStack,
  Text,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

// For the JSON data structure
type SynonymData = Record<string, string[]>;

// For the drawer props
interface SynonymDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  data: SynonymData;
}

const SynonymDrawer: React.FC<SynonymDrawerProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation("common");

  // Filter items based on search term (searches both keys and values)
  const filteredData = Object.entries(data || {}).filter(([title, items]) => {
    const term = searchTerm.toLowerCase();
    const titleMatch = title.toLowerCase().includes(term);
    const itemsMatch = items.some((item) => item.toLowerCase().includes(term));
    return titleMatch || itemsMatch;
  });

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="sm" placement="right">
      <DrawerOverlay />
      <DrawerContent bg="white">
        <DrawerCloseButton />
        <DrawerHeader fontWeight="bold" fontSize="lg" pb={2}>
          {t("synonymDrawer.synonymDictionary")}
        </DrawerHeader>

        <DrawerBody px={4}>
          {/* Search Input */}
          <InputGroup mb={6}>
            <Input
              placeholder={t("synonymDrawer.searchSynonyms")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              _focus={{
                borderColor: "gray.400",
                boxShadow: "none",
              }}
            />
          </InputGroup>

          {/* Categories List */}
          <VStack align="stretch" spacing={0}>
            {filteredData.map(([title, items]) => (
              <Box key={title} mb={4}>
                {/* Category Header */}
                <Box mb={2}>
                  <Text fontWeight="bold" fontSize="md" color="#1A1A1A">
                    {title}
                  </Text>
                </Box>

                {/* Items List */}
                <VStack align="stretch" spacing={0} pl={6}>
                  {items.length > 0 ? (
                    items.map((item, idx) => (
                      <Box
                        key={idx}
                        py={2}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Text fontSize="sm" color="gray.700">
                          {item}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text py={2} fontSize="sm" color="gray.500">
                      {t("synonymDrawer.noMatchesFound")}
                    </Text>
                  )}
                </VStack>
              </Box>
            ))}

            {filteredData.length === 0 && (
              <Text py={4} fontSize="sm" color="gray.500" textAlign="center">
                {t("synonymDrawer.noMatchesFound")}
              </Text>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default SynonymDrawer;
